import { useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { AcquisitionFunnel, type AcquisitionState } from "./AcquisitionFunnel";
import { UnsoldFunnel, type UnsoldState } from "./UnsoldFunnel";

/**
 * Public customer feedback funnel at /r/:token — mobile-first, no auth.
 *
 * Question set (Don Elwell, 2026-08-19 call; server side in migration 0019 +
 * services/survey_flow.ts). The server sends `survey.steps`, so the page never
 * decides the shape itself:
 *
 *   Q1  survey.howFoundUsPrompt  (campaigns with askHowFoundUs)
 *   Q2  "Did we do a great job?"  Yes / No
 *         Yes → "Any team members you'd like to recognize?" (optional) → Q3
 *         No  → private note to the manager, emailed to the store
 *   Q3  "Do you have a Yelp account?"  → Yelp or Google review CTA
 *   Exit  thanks + the gift-card entry confirmation
 *
 * The gift-card offer is ALSO shown up front, on every step above, from
 * survey.incentivePromptText — see rule 1 below.
 *
 * COMPLIANCE, two rules that live here:
 *  1. The incentive lines render for EVERY respondent — the up-front banner
 *     before any answer exists, and the exit line for everyone who finishes, with
 *     the "No" branch getting it on the same terms as "Yes". Google prohibits
 *     incentivized reviews and the FTC Consumer Review Rule prohibits
 *     sentiment-conditioned incentives; an entry earned by completing a survey
 *     is compliant, an entry earned by being happy is not.
 *  2. The server decides `showReviewCta`, never this page. Default (compliant)
 *     flow: everyone can still reach the public review CTA and unhappy
 *     customers ALSO get the manager form. Hard-gated campaigns
 *     (campaign.hardGateBelowThreshold — see the schema note) return
 *     showReviewCta=false below the threshold, and those respondents get only
 *     the manager form.
 *
 * The same /r/:token URL also serves two other funnels, each tagged by `kind`
 * in the GET payload so one fetch decides and the GET's first_clicked_at stamp
 * still fires exactly once:
 *  - `kind: "acquisition"` (vehicle_acquisition recipients) hands off to
 *    <AcquisitionFunnel> — "we want to buy your car".
 *  - `kind: "unsold"` (unsold_visit recipients) hands off to <UnsoldFunnel> —
 *    someone who visited and did NOT buy. That funnel carries NO review ask
 *    anywhere (see showsReviewCta() in services/survey_flow.ts); this page
 *    only routes to it, it never renders any of that funnel's steps itself.
 */

/** Mirrors DEFAULT_HOW_FOUND_US_PROMPT — used only if the request itself failed. */
const FALLBACK_HOW_FOUND_PROMPT = "What brought you into our dealership?";

/** Fallback list for a campaign with no configured options (server sends its own). */
const FALLBACK_HOW_FOUND_OPTIONS = [
  "Google search",
  "Facebook",
  "Friend or family referral",
  "Drove by the dealership",
  "Repeat customer",
  "Other",
];

interface SurveyConfig {
  steps: string[];
  askHowFoundUs: boolean;
  howFoundUsPrompt: string;
  howFoundUsOptions: string[];
  hasYelpOption: boolean;
  incentiveText: string | null;
  incentivePromptText: string | null;
}

interface FunnelState {
  kind?: "review" | "acquisition" | "unsold";
  state: "open" | "responded" | "expired";
  firstName?: string | null;
  dealerName?: string;
  satisfactionScore?: number | null;
  satisfactionThreshold?: number;
  survey?: SurveyConfig;
  hasYelpAccount?: boolean | null;
  showIncentive?: boolean;
  showReviewCta?: boolean;
  reviewUrl?: string;
}

type Step = "loading" | "expired" | "howFound" | "greatJob" | "recognition" | "yelp" | "thanks";

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

function Card({ children }: { children: ReactNode }) {
  return <div className="rounded-xl bg-white p-6 shadow-sm">{children}</div>;
}

export function FunnelPage() {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>("loading");
  const [funnel, setFunnel] = useState<FunnelState | null>(null);
  // Set instead of `funnel` when the token belongs to an acquisition campaign;
  // this page then renders that funnel and none of the review steps run.
  const [acquisition, setAcquisition] = useState<AcquisitionState | null>(null);
  // Same idea for an unsold_visit campaign — the up funnel, no review steps.
  const [unsold, setUnsold] = useState<UnsoldState | null>(null);
  const [howFound, setHowFound] = useState<string | null>(null);
  const [otherText, setOtherText] = useState("");
  const [greatJob, setGreatJob] = useState<boolean | null>(null);
  const [recognition, setRecognition] = useState("");
  const [feedback, setFeedback] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/public/funnel/${token}`);
        const data = (await res.json()) as FunnelState;
        if (cancelled) return;
        if (data.kind === "acquisition") {
          setAcquisition(data as unknown as AcquisitionState);
          return;
        }
        if (data.kind === "unsold") {
          setUnsold(data as unknown as UnsoldState);
          return;
        }
        setFunnel(data);
        if (data.state === "expired") setStep("expired");
        else if (data.state === "responded") {
          // Returning to a finished survey: show the exit step with whatever
          // destination they already earned, never re-ask the questions.
          const threshold = data.satisfactionThreshold ?? 4;
          setGreatJob(
            data.satisfactionScore == null ? null : data.satisfactionScore >= threshold
          );
          setStep("thanks");
        } else setStep(data.survey?.askHowFoundUs === false ? "greatJob" : "howFound");
      } catch {
        if (!cancelled) setStep("expired");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  /** Q1 + Q2 in one write — the server needs both to route the response. */
  async function submitResponse(didGreatJob: boolean) {
    if (submitting) return;
    setSubmitting(true);
    setGreatJob(didGreatJob);
    const answer =
      howFound === "Other" && otherText.trim() ? otherText.trim() : (howFound ?? undefined);
    try {
      const res = await post(`/api/public/funnel/${token}/respond`, {
        ...(funnel?.survey?.askHowFoundUs === false ? {} : { howFoundUs: answer ?? "Other" }),
        didGreatJob,
      });
      const data = (await res.json()) as {
        showReviewCta?: boolean;
        reviewUrl?: string;
        showIncentive?: boolean;
      };
      setFunnel((f) =>
        f
          ? {
              ...f,
              showReviewCta: data.showReviewCta,
              reviewUrl: data.reviewUrl,
              showIncentive: data.showIncentive,
            }
          : f
      );
    } catch {
      // Non-blocking: still advance so the customer isn't stuck on a spinner.
    }
    // Happy path collects the recognition note next; the unhappy path goes
    // straight to the manager form on the exit step.
    setStep(didGreatJob ? "recognition" : "thanks");
    setSubmitting(false);
  }

  async function submitRecognition(skip: boolean) {
    if (submitting) return;
    const text = recognition.trim();
    if (!skip && text) {
      setSubmitting(true);
      try {
        await post(`/api/public/funnel/${token}/recognition`, { teamMemberRecognition: text });
      } catch {
        // Recognition is a bonus, never a blocker — advance regardless.
      }
      setSubmitting(false);
    }
    // Q3 only exists when the campaign carries a Yelp destination; otherwise
    // asking "do you have a Yelp account?" would lead nowhere.
    setStep(funnel?.survey?.hasYelpOption ? "yelp" : "thanks");
  }

  async function submitYelp(hasYelpAccount: boolean) {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await post(`/api/public/funnel/${token}/yelp`, { hasYelpAccount });
      const data = (await res.json()) as { showReviewCta?: boolean; reviewUrl?: string };
      setFunnel((f) =>
        f
          ? {
              ...f,
              hasYelpAccount,
              showReviewCta: data.showReviewCta ?? f.showReviewCta,
              reviewUrl: data.reviewUrl ?? f.reviewUrl,
            }
          : f
      );
    } catch {
      // Fall through to the exit step with the Google default already set.
    }
    setStep("thanks");
    setSubmitting(false);
  }

  async function submitFeedback() {
    if (!feedback.trim() || submitting) return;
    setSubmitting(true);
    try {
      await post(`/api/public/funnel/${token}/feedback`, { privateFeedback: feedback.trim() });
      setFeedbackSent(true);
    } catch {
      // Leave the form up so they can retry.
    }
    setSubmitting(false);
  }

  // Acquisition tokens render an entirely different funnel — no review steps,
  // no satisfaction score, no gating logic below applies to them.
  if (acquisition) return <AcquisitionFunnel token={token} initial={acquisition} />;
  // Same for unsold_visit tokens — the up funnel, no review ask anywhere in it.
  if (unsold) return <UnsoldFunnel token={token} initial={unsold} />;

  const dealer = funnel?.dealerName ?? "our dealership";
  const hello = funnel?.firstName ? `Hi ${funnel.firstName}!` : "Hi there!";
  const options = funnel?.survey?.howFoundUsOptions?.length
    ? funnel.survey.howFoundUsOptions
    : FALLBACK_HOW_FOUND_OPTIONS;
  const howFoundPrompt = funnel?.survey?.howFoundUsPrompt?.trim() || FALLBACK_HOW_FOUND_PROMPT;
  const incentiveText = funnel?.survey?.incentiveText;
  // Up-front sweepstakes line. Rendered on every live survey step, identically,
  // before any answer exists — see the compliance note at the top of this file.
  const incentivePrompt = funnel?.survey?.incentivePromptText?.trim() || null;
  // Every step the customer is still working through — "recognition" included,
  // it is mid-survey. Not "thanks" (the exit line takes over there), not
  // "expired" and not "loading".
  const onSurveyStep =
    step === "howFound" || step === "greatJob" || step === "recognition" || step === "yelp";
  const yelpChosen = funnel?.hasYelpAccount === true;
  // Server decides. Falls back to true only because a failed request should not
  // silently suppress a review ask the campaign is entitled to.
  const showReviewCta = funnel?.showReviewCta !== false;
  // ALWAYS the /go link, never the raw review URL: /go stamps review_clicked
  // attribution (the only way a request is later matched to a posted review)
  // and re-checks the hard gate on replay before redirecting.
  const reviewHref = `/api/public/funnel/${token}/go`;

  return (
    <div className="flex min-h-screen flex-col items-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-xl font-semibold text-slate-900">{dealer}</h1>
        </div>

        {/* The offer, said before the survey rather than after. Deliberately
            OUTSIDE every step's Card and above all of them: it must not sit in
            a branch, and it must not sit next to the review CTA. It takes no
            answer as input, so it reads the same to a customer who will answer
            "Yes" and one who will answer "No". */}
        {onSurveyStep && incentivePrompt && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center">
            <p className="text-sm font-medium text-amber-900">{incentivePrompt}</p>
          </div>
        )}

        {step === "loading" && <p className="text-center text-slate-500">Loading…</p>}

        {step === "expired" && (
          <Card>
            <p className="text-center text-slate-700">This link is no longer active.</p>
            <p className="mt-2 text-center text-sm text-slate-500">
              We'd still love to hear from you — give us a call or stop by anytime.
            </p>
          </Card>
        )}

        {/* Q1 — sales campaigns only. */}
        {step === "howFound" && (
          <Card>
            <p className="text-lg font-medium text-slate-900">{hello}</p>
            <p className="mt-1 text-slate-600">{howFoundPrompt}</p>
            <div className="mt-4 flex flex-col gap-2">
              {options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setHowFound(opt)}
                  className={`rounded-lg border px-4 py-3 text-left text-sm transition ${
                    howFound === opt
                      ? "border-blue-600 bg-blue-50 font-medium text-blue-900"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                >
                  {opt}
                </button>
              ))}
              {howFound === "Other" && (
                <input
                  type="text"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Tell us how (optional)"
                  maxLength={80}
                  className="rounded-lg border border-slate-200 px-4 py-3 text-sm"
                />
              )}
            </div>
            <button
              type="button"
              disabled={!howFound}
              onClick={() => setStep("greatJob")}
              className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-3 font-medium text-white disabled:opacity-40"
            >
              Next
            </button>
          </Card>
        )}

        {/* Q2 — the question everything routes on. */}
        {step === "greatJob" && (
          <Card>
            <p className="text-lg font-medium text-slate-900">Did we do a great job?</p>
            <div className="mt-5 flex flex-col gap-3">
              <button
                type="button"
                disabled={submitting}
                onClick={() => void submitResponse(true)}
                className="rounded-lg bg-emerald-600 px-4 py-4 font-medium text-white disabled:opacity-50"
              >
                Yes, absolutely!
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => void submitResponse(false)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-4 font-medium text-slate-700 disabled:opacity-50"
              >
                No
              </button>
            </div>
            {submitting && <p className="mt-4 text-center text-sm text-slate-400">Saving…</p>}
          </Card>
        )}

        {/* Q2 "Yes" follow-up — optional, and skippable in one tap. */}
        {step === "recognition" && (
          <Card>
            <p className="text-lg font-medium text-slate-900">That's great to hear!</p>
            <p className="mt-1 text-slate-600">
              Any team members that you'd like to recognize?
            </p>
            <textarea
              value={recognition}
              onChange={(e) => setRecognition(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Who took great care of you?"
              className="mt-3 w-full rounded-lg border border-slate-200 p-3 text-sm"
            />
            <button
              type="button"
              disabled={submitting}
              onClick={() => void submitRecognition(false)}
              className="mt-3 w-full rounded-lg bg-blue-600 px-4 py-3 font-medium text-white disabled:opacity-40"
            >
              {recognition.trim() ? "Continue" : "Skip"}
            </button>
          </Card>
        )}

        {/* Q3 — only rendered when the campaign has a Yelp destination. */}
        {step === "yelp" && (
          <Card>
            <p className="text-lg font-medium text-slate-900">Do you have a Yelp account?</p>
            <p className="mt-1 text-sm text-slate-600">
              We'll point you to whichever one is easiest for you.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <button
                type="button"
                disabled={submitting}
                onClick={() => void submitYelp(true)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-4 font-medium text-slate-700 disabled:opacity-50"
              >
                Yes
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => void submitYelp(false)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-4 font-medium text-slate-700 disabled:opacity-50"
              >
                No
              </button>
            </div>
          </Card>
        )}

        {step === "thanks" && (
          <div className="flex flex-col gap-4">
            <Card>
              <p className="text-center text-lg font-medium text-slate-900">
                {greatJob === false
                  ? "Thank you for the honest feedback."
                  : "Thank you VERY much for your feedback!"}
              </p>
              {greatJob === false && (
                <p className="mt-2 text-center text-sm text-slate-600">
                  We take this seriously and want to make it right.
                </p>
              )}
              {/* Earned by finishing the survey — identical on both branches.
                  See the compliance note at the top of this file. */}
              {incentiveText && (
                <p className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-center text-sm text-amber-900">
                  {incentiveText}
                </p>
              )}
            </Card>

            {/* Q2 "No" → the manager form, on the exit step. */}
            {greatJob === false && !feedbackSent && (
              <Card>
                <p className="font-medium text-slate-900">Tell the manager directly</p>
                <p className="mt-1 text-sm text-slate-600">
                  Your note goes straight to {dealer}'s management team — not to a public page.
                </p>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={4}
                  maxLength={4000}
                  placeholder="What happened, and what can we do better?"
                  className="mt-3 w-full rounded-lg border border-slate-200 p-3 text-sm"
                />
                <button
                  type="button"
                  disabled={!feedback.trim() || submitting}
                  onClick={() => void submitFeedback()}
                  className="mt-3 w-full rounded-lg bg-slate-800 px-4 py-3 font-medium text-white disabled:opacity-40"
                >
                  Send to the manager
                </button>
              </Card>
            )}
            {feedbackSent && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center text-sm text-emerald-800">
                Sent. The management team will follow up with you shortly.
              </div>
            )}

            {/* Public review CTA — hidden only when a hard-gated campaign
                returns showReviewCta=false. */}
            {showReviewCta && (
              <Card>
                <p className="text-center text-sm text-slate-600">
                  {yelpChosen
                    ? "Great — here's our Yelp page. It only takes a minute."
                    : "Reviews help neighbors like you find us. Would you share your experience?"}
                </p>
                <a
                  href={reviewHref}
                  className="mt-3 inline-block w-full rounded-lg bg-blue-600 px-4 py-3 text-center font-medium text-white"
                >
                  {yelpChosen ? "Leave us a Yelp review" : "Leave us a Google review"}
                </a>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
